import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Printer, Wallet } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { adminApi, revendaApi } from '@/api/endpoints/admin';
import { useEntitlements } from '@/stores/entitlements';
import { cn } from '@/lib/utils';

type Periodo = 'mes' | 'ano';

function periodFrom(p: Periodo): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  if (p === 'mes') d.setDate(1);
  else d.setMonth(0, 1);
  return d.toISOString();
}
function fmtBRL(v: number) {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}
function downloadCsv(name: string, head: string[], rows: (string | number)[][]) {
  const csv = [head, ...rows]
    .map((l) => l.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';'))
    .join('\r\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

export function FinanceiroPage() {
  const papel = useEntitlements((s) => s.me?.user.papel ?? null);
  const isAdmin = papel === 'admin';
  const [periodo, setPeriodo] = useState<Periodo>('mes');
  const from = useMemo(() => periodFrom(periodo), [periodo]);

  const adminQ = useQuery({
    queryKey: ['financeiro', 'admin', from],
    queryFn: () => adminApi.financeiro(from),
    enabled: isAdmin,
  });
  const revQ = useQuery({
    queryKey: ['financeiro', 'revenda', from],
    queryFn: () => revendaApi.financeiro(from),
    enabled: !isAdmin,
  });

  const loading = isAdmin ? adminQ.isLoading : revQ.isLoading;
  const error = isAdmin ? adminQ.error : revQ.error;

  const onExport = () => {
    if (isAdmin && adminQ.data) {
      downloadCsv(`financeiro-revendas-${periodo}.csv`, ['revenda', 'clientes', 'locacoes', 'receita'],
        adminQ.data.revendas.map((r) => [r.nome, r.clientes, r.locacoes, r.receita.toFixed(2).replace('.', ',')]));
    } else if (revQ.data) {
      downloadCsv(`financeiro-clientes-${periodo}.csv`, ['cliente', 'locacoes', 'horas', 'receita'],
        revQ.data.clientes.map((c) => [c.nome, c.locacoes, c.horas, c.receita.toFixed(2).replace('.', ',')]));
    }
  };

  return (
    <div className="space-y-5 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Financeiro</h1>
          <p className="text-sm text-muted-foreground">
            {isAdmin ? 'Receita consolidada por revenda (locadora dos clientes).' : 'Faturamento dos seus clientes (locadora).'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full border border-border bg-card p-0.5 text-xs font-semibold">
            {(['mes', 'ano'] as Periodo[]).map((p) => (
              <button key={p} type="button" onClick={() => setPeriodo(p)}
                className={cn('rounded-full px-3 py-1', periodo === p ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent')}>
                {p === 'mes' ? 'Mês' : 'Ano'}
              </button>
            ))}
          </div>
          <button type="button" onClick={onExport} className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1.5 text-xs font-semibold hover:bg-accent"><Download className="h-3.5 w-3.5" /> CSV</button>
          <button type="button" onClick={() => window.print()} className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1.5 text-xs font-semibold hover:bg-accent"><Printer className="h-3.5 w-3.5" /> PDF</button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : error ? (
        <p className="text-sm text-destructive">{(error as Error).message}</p>
      ) : isAdmin && adminQ.data ? (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Stat label="Receita total" value={fmtBRL(adminQ.data.total_receita)} />
            <Stat label="Locações" value={String(adminQ.data.total_locacoes)} />
            <Stat label="Revendas" value={String(adminQ.data.revendas.length)} />
          </div>
          <FinTable
            head={['Revenda', 'Clientes', 'Locações', 'Receita']}
            rows={adminQ.data.revendas.map((r) => [r.nome, String(r.clientes), String(r.locacoes), fmtBRL(r.receita)])}
            empty="Nenhuma revenda cadastrada."
          />
        </>
      ) : revQ.data ? (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Stat label="Receita total" value={fmtBRL(revQ.data.total_receita)} />
            <Stat label="Locações" value={String(revQ.data.total_locacoes)} />
            <Stat label="Horas locadas" value={`${revQ.data.total_horas}h`} />
          </div>
          <FinTable
            head={['Cliente', 'Locações', 'Horas', 'Receita']}
            rows={revQ.data.clientes.map((c) => [c.nome, String(c.locacoes), `${c.horas}h`, fmtBRL(c.receita)])}
            empty="Nenhum cliente vinculado."
          />
        </>
      ) : null}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="p-3">
        <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground"><Wallet className="h-3.5 w-3.5" /> {label}</div>
        <p className="mt-1 text-2xl font-bold leading-tight">{value}</p>
      </CardContent>
    </Card>
  );
}

function FinTable({ head, rows, empty }: { head: string[]; rows: string[][]; empty: string }) {
  if (rows.length === 0) return <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">{empty}</CardContent></Card>;
  return (
    <Card>
      <CardContent className="p-0">
        {/* rolagem horizontal: tabela larga nao pode empurrar a pagina inteira no celular */}
        <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="border-b border-border text-left text-muted-foreground">
            <tr>{head.map((h, i) => <th key={h} className={cn('px-3 py-2 font-semibold', i > 0 && 'text-right')}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r, ri) => (
              <tr key={ri} className="border-b border-border/50 last:border-0">
                {r.map((c, ci) => <td key={ci} className={cn('px-3 py-2', ci > 0 && 'text-right', ci === 0 && 'font-medium', ci === r.length - 1 && 'font-semibold')}>{c}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </CardContent>
    </Card>
  );
}
